// src/contracts/feedback/schemas.ts
//
// 피드백 폼 zod 스키마 (zod 4.1).
//
// 길이 상한은 **서버 검증과 같은 값**이어야 한다(총괄 확정):
//   제목 120자 / 본문 20,000자 / 댓글 2,000자.
// 서버는 룬(rune) 기준으로 세고 JS 의 String.length 는 UTF-16 코드유닛
// 기준이라 한글·이모지에서 미세하게 다를 수 있다. 이모지는 JS 쪽이 더
// 크게 세므로(서로게이트 페어=2) 프론트가 먼저 막는 방향이라 안전하다.

import { z } from "zod";

export const feedbackTypeSchema = z.enum(["bug", "improvement", "other"]);
export const feedbackStatusSchema = z.enum([
  "open",
  "in_progress",
  "done",
  "declined",
]);

/** 서버 검증과 동일한 상한. 폼 카운터도 이 상수를 읽는다. */
export const FEEDBACK_LIMITS = {
  title: 120,
  body: 20000,
  comment: 2000,
} as const;

export const feedbackPostFormSchema = z.object({
  // ToggleButtonGroup 이 항상 유효한 값을 들고 있어 실제로는 도달하지 않지만,
  // 폼 상태가 어떤 이유로든 깨졌을 때 영문 기본 메시지가 새어 나가지 않게 한다.
  category: z.enum(["bug", "improvement", "other"], {
    error: "유형을 선택해 주세요.",
  }),
  title: z
    .string()
    .trim()
    .min(2, "제목을 2자 이상 입력해 주세요.")
    .max(FEEDBACK_LIMITS.title, "제목은 120자를 넘을 수 없습니다."),
  body: z
    .string()
    .trim()
    .min(5, "내용을 5자 이상 입력해 주세요.")
    .max(FEEDBACK_LIMITS.body, "내용은 20,000자를 넘을 수 없습니다."),
});
export type FeedbackPostFormValues = z.infer<typeof feedbackPostFormSchema>;

export const feedbackCommentFormSchema = z.object({
  body: z
    .string()
    .trim()
    .min(1, "댓글을 입력해 주세요.")
    .max(FEEDBACK_LIMITS.comment, "댓글은 2,000자를 넘을 수 없습니다."),
});
export type FeedbackCommentFormValues = z.infer<
  typeof feedbackCommentFormSchema
>;

export const feedbackStatusChangeSchema = z.object({
  status: feedbackStatusSchema,
});

/**
 * 댓글 본문 즉시 검증 — CommentComposer / CommentList 의 인라인 편집처럼
 * react-hook-form 을 붙이지 않는 한 줄 폼에서 쓴다.
 * 성공이면 null, 실패면 사용자에게 보여줄 메시지를 돌려준다.
 */
export const validateCommentBody = (body: string): string | null => {
  const parsed = feedbackCommentFormSchema.safeParse({ body });
  if (parsed.success) return null;
  return parsed.error.issues[0]?.message ?? "댓글을 확인해 주세요.";
};
